import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { STORAGE_KEYS } from '#/shared/lib/storage'

export const SIDE_PANEL_MIN_WIDTH = 348
export const SIDE_PANEL_MAX_WIDTH = 560
export const SIDE_PANEL_DEFAULT_WIDTH = 380

const clampWidth = (width: number): number =>
  Math.min(
    SIDE_PANEL_MAX_WIDTH,
    Math.max(SIDE_PANEL_MIN_WIDTH, Math.round(width)),
  )

interface SidePanelState {
  isOpen: boolean
  /** Persisted panel width in px (clamped to the min/max range). */
  width: number
  /** Transient: true while the user is dragging the resize handle. */
  resizing: boolean
  open: () => void
  close: () => void
  toggle: () => void
  setWidth: (width: number) => void
  setResizing: (resizing: boolean) => void
}

export const useSidePanelStore = create<SidePanelState>()(
  persist(
    (set) => ({
      isOpen: false,
      width: SIDE_PANEL_DEFAULT_WIDTH,
      resizing: false,
      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      toggle: () => set((state) => ({ isOpen: !state.isOpen })),
      setWidth: (width) => set({ width: clampWidth(width) }),
      setResizing: (resizing) => set({ resizing }),
    }),
    {
      name: STORAGE_KEYS.sidePanel.key,
      storage: createJSONStorage(() => localStorage),
      // Persist open state + width; `resizing` is transient.
      partialize: (state) => ({ isOpen: state.isOpen, width: state.width }),
    },
  ),
)
